export const HOSTED_PURCHASE_STORAGE_KEY = 'tt_ga4_hosted_purchase';

type HostedPurchaseClaim = {
  sessionId: string;
  transactionId: string;
};

// Reading window.localStorage itself throws when the browser blocks storage.
function availableStorages(): Storage[] {
  const storages: Storage[] = [];
  for (const get of [() => window.localStorage, () => window.sessionStorage]) {
    try {
      storages.push(get());
    } catch {
      // Use whichever storage is left.
    }
  }
  return storages;
}

function readClaims(storage: Storage): HostedPurchaseClaim[] {
  try {
    const raw = storage.getItem(HOSTED_PURCHASE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is HostedPurchaseClaim =>
        !!entry &&
        typeof entry === 'object' &&
        typeof (entry as HostedPurchaseClaim).sessionId === 'string' &&
        typeof (entry as HostedPurchaseClaim).transactionId === 'string',
    );
  } catch {
    return [];
  }
}

/** First caller for this Stripe session or transaction id sends the purchase. */
export function claimHostedPurchase(sessionId: string, transactionId: string): boolean {
  if (!sessionId || !transactionId) return false;
  const storages = availableStorages();
  // Merge both stores so a claim saved only in sessionStorage survives the next write.
  const claims = new Map(storages.flatMap(readClaims).map((entry) => [entry.sessionId, entry]));
  const matches = (entry: HostedPurchaseClaim) =>
    entry.sessionId === sessionId || entry.transactionId === transactionId;
  if ([...claims.values()].some(matches)) return false;

  const serialized = JSON.stringify([...claims.values(), { sessionId, transactionId }].slice(-30));
  for (const storage of storages) {
    try {
      storage.setItem(HOSTED_PURCHASE_STORAGE_KEY, serialized);
    } catch {
      // Private mode or a full quota can block one store; the other may still work.
    }
  }
  return true;
}
